import React, { useState, useEffect } from 'react';
import { Search, Bell, HelpCircle, CheckCircle, AlertCircle, DollarSign, Filter, Eye, Edit, Check, X } from 'lucide-react';
import './Hrtimesheet.css';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

// ✅ FIXED: Backend URL determination function (same as Login.jsx)
const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.leave-portal.manurevasolutions.com';
  }
  
  return 'http://localhost:5000';
};

const BACKEND_URL = getBackendUrl();

// ✅ FIXED: API call with correct backend URL
const authenticatedApiCall = async (url, options = {}) => {
  // Ensure URL starts with backend base URL
  const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  
  console.log(`📡 API Call: ${options.method || 'GET'} ${fullUrl}`);
  console.log(`🌐 Environment: ${process.env.NODE_ENV}`);
  console.log(`🔗 Backend URL: ${BACKEND_URL}`);
  
  const response = await fetch(fullUrl, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`❌ API Error (${response.status}):`, errorText);
    throw new Error(`API call failed: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

const useAuth = () => {
  return {
    user: { name: 'HR Manager', role: 'HR', profile_picture: null },
    handleLogout: () => window.location.href = '/',
    isAuthenticated: true,
    isLoading: false
  };
};

const TimesheetPage = () => {
  const { user, handleLogout, isAuthenticated, isLoading } = useAuth();
  const location = { pathname: '/Hrtimesheet' };
  
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
  pendingCount: 0,
  approvedThisMonth: 0,
  totalHours: 0,
  assignedConsultants: 0  // ✅ ADD THIS
});
  const [actionLoading, setActionLoading] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // Current applied filter
  const [selectedDateFilter, setSelectedDateFilter] = useState('all'); // ✅ Temporary selection before applying

  // View/Edit popup states
  const [showViewPopup, setShowViewPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  
  // ✅ NEW: Day-by-day edit data structure
  const [editDayEntries, setEditDayEntries] = useState([]);

  // ✅ NEW: Approve popup state
  const [showApprovePopup, setShowApprovePopup] = useState(false);
  const [approveComments, setApproveComments] = useState('');

  const calculateStats = (timesheetData) => {
  const currentMonth = new Date().toISOString().substring(0, 7); // e.g., "2025-12"
  
  console.log('📊 [STATS CALCULATION]');
  console.log('Current Month:', currentMonth);
  console.log('Total Timesheets:', timesheetData.length);
  
  // ✅ FIX 1: Pending Approvals - Count ALL pending/edited timesheets
  const pendingTimesheets = timesheetData.filter(ts => 
    ts.status === 'pending' || ts.status === 'edited'
  );
  const pending = pendingTimesheets.length;
  
  console.log(`✅ Pending Count: ${pending} timesheets`);
  
  // ✅ FIX 2: Approved This Month - Count APPROVED timesheets in current month
  // A timesheet is "approved this month" if ANY of these dates fall in current month:
  // 1. updated_at (when it was approved)
  // 2. approved_at (explicit approval timestamp)
  // 3. week_start (when the work period started)
  const approvedThisMonth = timesheetData.filter(ts => {
    // Must be approved status
    if (ts.status !== 'approved') {
      return false;
    }
    
    // Check multiple date fields to find month match
    const datesToCheck = [
      ts.updated_at,      // When approved by HR
      ts.approved_at,     // Explicit approval timestamp
      ts.submitted_at,    // When submitted
      ts.week_start       // Work period start
    ];
    
    // Check if ANY date matches current month
    for (const dateField of datesToCheck) {
      if (dateField) {
        const dateMonth = dateField.substring(0, 7);
        if (dateMonth === currentMonth) {
          console.log(`✅ ${ts.user_name} - INCLUDED (${dateMonth} matches ${currentMonth})`);
          return true;
        }
      }
    }
    
    console.log(`❌ ${ts.user_name} - EXCLUDED (no dates match ${currentMonth})`);
    return false;
  }).length;
  
  console.log(`📈 Approved This Month: ${approvedThisMonth} timesheets`);
  
  // ✅ FIX 3: Total Hours - Sum ALL pending hours
  const totalHours = pendingTimesheets.reduce((sum, ts) => sum + (parseFloat(ts.total_hours) || 0), 0);
  
  console.log(`⏰ Total Pending Hours: ${totalHours}`);
  console.log('---');
  
  return {
    pendingCount: pending,
    approvedThisMonth: approvedThisMonth,
    totalHours: totalHours
  };
};

  const fetchTimesheets = async (isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner for initial load, not background refreshes
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setError(null);
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (dateFilter && dateFilter !== 'all') {
        queryParams.append('filter', dateFilter);
      }
      
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      console.log('🔄 Fetching timesheets from:', `${BACKEND_URL}/api/timesheet/hr/pending-approvals${queryString}`);
      
      const pendingResponse = await authenticatedApiCall(`/api/timesheet/hr/pending-approvals${queryString}`);
      
      if (!pendingResponse) {
        throw new Error('No response received from server');
      }
      
      if (pendingResponse.error) {
        throw new Error(pendingResponse.error);
      }
      
      const allSubmissions = pendingResponse.submissions || [];
      
      console.log('✅ Fetched timesheets:', allSubmissions.length, isBackgroundRefresh ? '(background)' : '(initial)');
      
      setTimesheets(allSubmissions);
      
      const calculatedStats = calculateStats(allSubmissions);
      // Extract assigned consultant count from response
const assignedCount = pendingResponse.assigned_consultants || 0;
setStats({
  ...calculatedStats,
  assignedConsultants: assignedCount
});
      
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Failed to fetch timesheets:', err);
      if (!isBackgroundRefresh) {
        setError('Failed to load timesheets. Please try again.');
        setStats({
          pendingCount: 0,
          approvedThisMonth: 0,
          totalHours: 0
        });
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchTimesheets();
    }
  }, [isAuthenticated, dateFilter]);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        fetchTimesheets();
      }, 60000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleViewClick = (timesheet) => {
    console.log('👀 View clicked for timesheet:', timesheet);
    setSelectedTimesheet(timesheet);
    setShowViewPopup(true);
  };

  // ✅ FIXED: Edit Button Click - Prepare day-by-day entries for editing
  const handleEditClick = (timesheet) => {
    console.log('✏️ Edit clicked for timesheet:', timesheet);
    setSelectedTimesheet(timesheet);
    
    // ✅ Transform week data into editable day-by-day entries
    const dayEntries = (timesheet.entries || []).map(entry => ({
      id: entry.id,
      date: entry.date,
      hours: entry.hours || 0,
      project_title: entry.project_title || 'General Work',
      description: entry.description || '',
      from_time: entry.from_time || '09:00',
      to_time: entry.to_time || '17:00'
    }));
    
    // Sort by date
    dayEntries.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    setEditDayEntries(dayEntries);
    setShowEditPopup(true);
  };

  const handleCloseViewPopup = () => {
    setShowViewPopup(false);
    setSelectedTimesheet(null);
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setSelectedTimesheet(null);
    setEditDayEntries([]);
  };

  const handleCloseApprovePopup = () => {
    setShowApprovePopup(false);
    setSelectedTimesheet(null);
    setApproveComments('');
  };

  // ✅ NEW: Handle individual day entry change
  const handleDayEntryChange = (dayIndex, field, value) => {
    setEditDayEntries(prev => {
      const updated = [...prev];
      updated[dayIndex] = {
        ...updated[dayIndex],
        [field]: value
      };
      return updated;
    });
  };

  // ✅ FIXED: Save day-by-day changes with better error handling
  const handleSaveDayByDayEdit = async () => {
    if (!selectedTimesheet || editDayEntries.length === 0) {
      alert('No data to save');
      return;
    }

    // Validate all entries
    for (let i = 0; i < editDayEntries.length; i++) {
      const entry = editDayEntries[i];
      if (!entry.hours || entry.hours <= 0 || entry.hours > 24) {
        alert(`Invalid hours for ${entry.date}. Must be between 0 and 24.`);
        return;
      }
      if (!entry.project_title || entry.project_title.trim() === '') {
        alert(`Project title is required for ${entry.date}`);
        return;
      }
    }

    try {
      setActionLoading(selectedTimesheet.submission_id);
      
      const payload = {
        entries: editDayEntries.map(entry => ({
          id: entry.id,
          date_key: entry.date,
          hours: parseFloat(entry.hours),
          project_title: entry.project_title.trim(),
          description: entry.description.trim(),
          from_time: entry.from_time,
          to_time: entry.to_time
        }))
      };
      
      console.log('💾 Saving edited timesheet:', {
        submission_id: selectedTimesheet.submission_id,
        entry_count: editDayEntries.length,
        payload: payload
      });
      
      // ✅ CRITICAL FIX: Use HR edit endpoint
      const response = await authenticatedApiCall(
        `/api/timesheet/hr/edit/${selectedTimesheet.submission_id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload)
        }
      );
      
      console.log('✅ Save response:', response);
      
      alert(`✅ ${response.message || 'Timesheet updated successfully!'}\nUpdated ${response.updated_count || editDayEntries.length} day entries.`);
      handleCloseEditPopup();
      
      // Refresh data
      await fetchTimesheets();
      
    } catch (error) {
      console.error('❌ Error saving edits:', error);
      
      // Show more detailed error message
      let errorMessage = 'Failed to save changes.';
      if (error.message) {
        errorMessage += `\n\nError: ${error.message}`;
      }
      errorMessage += '\n\nPlease check the console for details and try again.';
      
      alert(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (submissionId) => {
    if (!window.confirm('Are you sure you want to approve this timesheet?')) {
      return;
    }
    
    // ✅ UPDATED: Open approve popup instead of direct approval
    const timesheet = timesheets.find(ts => ts.submission_id === submissionId);
    if (timesheet) {
      setSelectedTimesheet(timesheet);
      setApproveComments('');
      setShowApprovePopup(true);
    }
  };

  // ✅ NEW: Confirm approval with comments
  const handleConfirmApprove = async () => {
    if (!selectedTimesheet) return;

    setActionLoading(selectedTimesheet.submission_id);

    try {
      const response = await authenticatedApiCall(
        `/api/timesheet/hr/approve/${selectedTimesheet.submission_id}`, 
        {
          method: 'POST',
          body: JSON.stringify({ comments: approveComments || 'Approved by HR' })
        }
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      const updatedTimesheets = timesheets.map(ts => {
        if (ts.submission_id === selectedTimesheet.submission_id) {
          return {
            ...ts,
            status: 'approved',
            updated_at: new Date().toISOString()
          };
        }
        return ts;
      });
      
      setTimesheets(updatedTimesheets);
      
      const newStats = calculateStats(updatedTimesheets);
      setStats(newStats);
      
      alert('✅ Timesheet approved successfully!');
      handleCloseApprovePopup();
      
      await fetchTimesheets();
      
    } catch (error) {
      console.error('❌ Error approving timesheet:', error);
      alert(`Failed to approve: ${error.message || 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDateFilterChange = (e) => {
    const newFilter = e.target.value;
    setSelectedDateFilter(newFilter); // Update temporary selection
  };

  // Apply the selected filter
  const handleApplyFilter = () => {
    setDateFilter(selectedDateFilter); // Apply the temporary selection
    fetchTimesheets(); // Fetch data with the new filter
  };

  // Get display name for the current filter
  const getFilterDisplayName = (filterValue) => {
    switch (filterValue) {
      case 'current_month':
        return 'Current Month';
      case 'last_month':
        return 'Last Month';
      case 'this_year':
        return 'This Year';
      case 'all':
        return 'All Time';
      default:
        return 'selected period';
    }
  };

  const filteredTimesheets = timesheets.filter(ts => {
    if (activeTab === 'pending' && ts.status !== 'pending' && ts.status !== 'edited') return false;
    if (activeTab === 'approved' && ts.status !== 'approved') return false;
    
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const matchesName = ts.user_name?.toLowerCase().includes(searchLower);
      const matchesProjects = ts.projects?.some(p => p.toLowerCase().includes(searchLower));
      
      return matchesName || matchesProjects;
    }
    
    return true;
  });

  const pendingCount = timesheets.filter(ts => ts.status === 'pending' || ts.status === 'edited').length;
  const approvedCount = timesheets.filter(ts => ts.status === 'approved').length;
  const totalTimesheets = timesheets.length;

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      handleLogout();
    }
  };

  if (isLoading || loading) {
    return (
      <div className="hr-consult-timesheet-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{fontSize: '24px', marginBottom: '10px'}}>⏳</div>
          <div>Loading timesheets...</div>
          <div style={{fontSize: '12px', color: '#666', marginTop: '8px'}}>
            Connecting to: {BACKEND_URL}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="hr-consult-timesheet-container">
      <div className="consult-dashboard-container" style={{backgroundColor: '#ECEDF0', height:'100%'}}>
        <div className="hr-consult-right">   
          {/* Header - Commented out as in original */}
          {/* <div className="hr-consult-header">...</div> */}

          {/* Main Content */}
          <main className="hr-consult-main-content">
            <div className="hr-consult-page-header">
              <div className="hr-consult-page-title">Timesheet Management</div>
              <div className="hr-consult-search-bar" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <img src='/Images/Search.png' alt='🔍' className="hr-consult-search-icon" style={{ position: 'absolute', left: '12px', zIndex: 1 }} />
                <input 
                  type="text" 
                  placeholder="Search by name or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                />
              </div>
            </div>
            {/* Stats Cards */}
            <div className="hr-consult-stats-grid">
              <div 
                className="hr-consult-stat-card" 
                onClick={() => setActiveTab('pending')}
                style={{cursor: 'pointer', transition: 'transform 0.2s', ':hover': {transform: 'scale(1.02)'}}}
              >
                <div className="hr-consult-stat-header">
                  <span className="hr-consult-stat-label" style={{color:'#2196F3'}}>Pending Approvals</span>
                  <img src='/Images/exelaratory.png' alt='!' className="hr-consult-stat-help-icon"/>
                </div>
                <div className="hr-consult-stat-value">{stats.pendingCount}</div>
                <div className="hr-consult-stat-footer">Awaiting review</div>
              </div>

              <div 
                className="hr-consult-stat-card hr-consult-stat-success"
                onClick={() => setActiveTab('approved')}
                style={{cursor: 'pointer', transition: 'transform 0.2s', ':hover': {transform: 'scale(1.02)'}}}
              >
                <div className="hr-consult-stat-header">
                  <span className="hr-consult-stat-label" style={{color:'#009688'}}>Approved This Month</span>
                  <img src="/Images/tick.png" alt="✓" className="hr-consult-stat-help-icon"/>
                </div>
                <div className="hr-consult-stat-value">{stats.approvedThisMonth}</div>
                <div className="hr-consult-stat-footer">Timesheets Processed</div>
              </div>

              <div className="hr-consult-stat-card hr-consult-stat-warning">
                <div className="hr-consult-stat-header">
                  <span className="hr-consult-stat-label" style={{color:'#FF0004'}}>Total Hours</span>
                  <img src='/Images/clock-red.png' alt='⏰' className="hr-consult-stat-help-icon"/>
                </div>
                <div className="hr-consult-stat-value">{stats.totalHours.toFixed(1)}</div>
                <div className="hr-consult-stat-footer">Pending this month</div>
              </div>

              <div className="hr-consult-stat-card hr-consult-stat-warning">
                <div className="hr-consult-stat-header">
                  <span className="hr-consult-stat-label" style={{color:'#9C27B0'}}>My Consultants</span>
                  <img src='/Images/Totalemp.png' alt='👥' className="hr-consult-stat-help-icon"/>
                </div>
                <div className="hr-consult-stat-value">{stats.assignedConsultants}</div>
                <div className="hr-consult-stat-footer">Assigned to me</div>
              </div>
            </div>
            {/* <div className="hr-consult-stat-card" style={{borderLeft: '4px solid #9C27B0'}}>
  <div className="hr-consult-stat-header">
    <span className="hr-consult-stat-label" style={{color:'#9C27B0'}}>My Consultants</span>
    <img src='/Images/tabler_users.png' alt='👥' className="hr-consult-stat-help-icon"/>
  </div>
  <div className="hr-consult-stat-value">{stats.assignedConsultants}</div>
  <div className="hr-consult-stat-footer">Assigned to me</div>
</div> */}

            {/* Filters and Actions */}
            <div className="hr-consult-filter-section">
              <div className="hr-consult-search-timesheet">
                <div style={{position: 'relative', width: '100%'}}>
                  <Search className="hr-consult-search-icon" size={18} style={{position: 'absolute', left: '12px', top: '70%', transform: 'translateY(-50%)', color: '#94a3b8', zIndex: 1}} />
                  <input 
                    type="text" 
                    placeholder="Search by name or project..."
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    style={{width: '100%', paddingLeft: '40px', paddingRight: '12px', height: '42px', border: '1px solid #2A2A2A', borderRadius: '8px', fontSize: '14px'}}
                  />
                </div>              </div>
              <div className="hr-consult-filter-actions">
                <select 
                  className="hr-consult-filter-btn" 
                  value={selectedDateFilter}
                  onChange={handleDateFilterChange}
                >
                  <option value="current_month">Current Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_year">This Year</option>
                  <option value="all">All Time</option>
                </select>
                <button className="hr-consult-filter-btn" onClick={handleApplyFilter} disabled={loading} style={{height: '42px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer'}}>
                  <Filter size={16} />
                  {loading ? 'Refreshing...' : 'Filter'}
                </button>
              </div>
            </div>

            <div className="hr-consult-tabs-list">
              {/* Tabs */}
              <div className="hr-consult-tabs">
                <button 
                  className={`hr-consult-tab ${activeTab === 'pending' ? 'hr-consult-active' : ''}`}
                  onClick={() => setActiveTab('pending')}
                >
                  Pending ({pendingCount})
                </button>
                <button 
                  className={`hr-consult-tab ${activeTab === 'approved' ? 'hr-consult-active' : ''}`}
                  onClick={() => setActiveTab('approved')}
                >
                  Approved ({approvedCount})
                </button>
                <button 
                  className={`hr-consult-tab ${activeTab === 'all' ? 'hr-consult-active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  All Timesheets ({totalTimesheets})
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{
                  background: '#fee',
                  color: '#c33',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  textAlign: 'center'
                }}>
                  {error}
                  <div style={{fontSize: '12px', marginTop: '8px', color: '#999'}}>
                    Backend: {BACKEND_URL}
                  </div>
                </div>
              )}

              {/* Timesheet List */}
              <div className="hr-consult-timesheet-list">
                {filteredTimesheets.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#666',
                    fontSize: '16px'
                  }}>
                    {searchTerm 
                      ? '🔍 No timesheets found matching your search.' 
                      : activeTab === 'pending'
                        ? '📋 No pending timesheet submissions found.'
                        : activeTab === 'approved'
                          ? '✅ No approved timesheets found.'
                          : dateFilter !== 'all'
                            ? `📋 No timesheets found for ${getFilterDisplayName(dateFilter)}.`
                            : '📋 No timesheets available.'}
                  </div>
                ) : (
                  filteredTimesheets.map((timesheet) => (
                    <div key={timesheet.submission_id} className="hr-consult-timesheet-card">
                      <div className="hr-consult-timesheet-header">
                        <div className="hr-consult-employee-info">
                          <div className="hr-consult-employee-avatar" style={{
                            backgroundColor: timesheet.user_id % 3 === 0 ? '#9333ea' : 
                                           timesheet.user_id % 3 === 1 ? '#ec4899' : '#10b981'
                          }}>
                            <span>
                              {timesheet.user_name?.split(' ').map(n => n[0]).join('') || 'U'}
                            </span>
                          </div>
                          <div className="hr-consult-employee-details">
                            <h3 className="hr-consult-employee-name">{timesheet.user_name}</h3>
                            <p className="hr-consult-employee-email">{timesheet.user_email}</p>
                          </div>
                        </div>
                        <div className={`hr-consult-status-badge hr-consult-${timesheet.status === 'edited' ? 'pending' : timesheet.status}`}>
                          {(timesheet.status === 'pending' || timesheet.status === 'edited') ? '⏳ Pending' : '✅ Approved'}
                        </div>
                      </div>

                      <div className="hr-consult-timesheet-body">
                        <div className="hr-consult-timesheet-info-grid">
                          <div className="hr-consult-info-item">
                            <span className="hr-consult-info-label">Period</span>
                            <span className="hr-consult-info-value">{timesheet.week_display}</span>
                          </div>
                          <div className="hr-consult-info-item">
                            <span className="hr-consult-info-label">Total Hours</span>
                            <span className="hr-consult-info-value">{timesheet.total_hours}h</span>
                          </div>
                        </div>
                        <div className="hr-consult-timesheet-info-grid">
                          <div className="hr-consult-info-item">
                            <span className="hr-consult-info-label">Billable Hours</span>
                            <span className="hr-consult-info-value">{timesheet.billable_hours}h</span>
                          </div>
                        </div>

                        <div className="hr-consult-projects-section">
                          <span className="hr-consult-projects-label">Projects</span>
                          <div className="hr-consult-project-tags">
                            {timesheet.projects?.map((project, index) => (
                              <span key={index} className="hr-consult-project-tag">{project}</span>
                            ))}
                          </div>
                        </div>

                        <div className="hr-consult-timesheet-footer">
                          <span className="hr-consult-submitted-id">
                            Submitted: {new Date(timesheet.submitted_at).toLocaleDateString()}
                          </span>
                          <div className="hr-consult-action-buttons">
                            <button 
                              className="hr-consult-action-btn hr-consult-view-btn"
                              onClick={() => handleViewClick(timesheet)}
                            >
                              <Eye size={16} />
                              View
                            </button>
                            {(timesheet.status === 'pending' || timesheet.status === 'edited') && (
                              <>
                                <button 
                                  className="hr-consult-action-btn hr-consult-edit-btn"
                                  onClick={() => handleEditClick(timesheet)}
                                >
                                  <Edit size={16} />
                                  Edit
                                </button>
                                <button 
                                  className="hr-consult-action-btn hr-consult-approve-btn"
                                  onClick={() => handleApprove(timesheet.submission_id)}
                                  disabled={actionLoading === timesheet.submission_id}
                                >
                                  <Check size={16} />
                                  {actionLoading === timesheet.submission_id ? 'Processing...' : 'Approve'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>    
          </main>
        </div>

        {/* View Popup Modal */}
        {showViewPopup && selectedTimesheet && (
          <div className="consult-popup-overlay" onClick={handleCloseViewPopup}>
            <div className="consult-popup-content" onClick={(e) => e.stopPropagation()} style={{
              width: window.innerWidth <= 768 ? '90%' : window.innerWidth <= 1024 ? '85%' : '700px',
              maxWidth: '700px',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '20px' : '32px',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px',
                borderBottom: '2px solid #2196F3',
                paddingBottom: '16px'
              }}>
                <h2 style={{ 
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#2196F3',
                  fontFamily: 'Albert Sans',
                }}>Timesheet Details</h2>
                <button 
                  onClick={handleCloseViewPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Employee Information */}
              <div style={{
                background: '#f8f9fa',
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{color: '#2196F3', marginBottom: '12px', fontSize: window.innerWidth <= 768 ? '16px' : '18px'}}>Employee Information</h3>
                <div style={{display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : window.innerWidth <= 1024 ? 'repeat(2, 1fr)' : '1fr 1fr', gap: '12px', fontSize: window.innerWidth <= 768 ? '13px' : '14px'}}>
                  <div>
                    <strong>Name:</strong> {selectedTimesheet.user_name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedTimesheet.user_email}
                  </div>
                  <div>
                    <strong>Status:</strong> 
                    <span style={{
                      marginLeft: '8px',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      background: selectedTimesheet.status === 'approved' ? '#4caf50' : '#ff9800',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {selectedTimesheet.status === 'pending' || selectedTimesheet.status === 'edited' ? '⏳ Pending' : '✅ Approved'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Week Summary */}
              <div style={{
                background: '#e3f2fd',
                padding: window.innerWidth <= 768 ? '16px' : '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h3 style={{color: '#2196F3', marginBottom: '12px', fontSize: window.innerWidth <= 768 ? '16px' : '18px'}}>Week Summary</h3>
                <div style={{display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : window.innerWidth <= 1024 ? 'repeat(2, 1fr)' : '1fr 1fr', gap: '20px', fontSize: window.innerWidth <= 768 ? '13px' : '14px'}}>
                  <div>
                    <strong>Period:</strong><br/>{selectedTimesheet.week_display}
                  </div>
                  <div style={{marginLeft:"100px"}} >
                    <strong>Total Hours:</strong><br/>{selectedTimesheet.total_hours}h
                  </div>
                </div>
              </div>

              {/* Projects */}
              <div style={{marginBottom: '20px'}}>
                <h3 style={{color: '#2196F3', marginBottom: '12px'}}>Projects Worked On</h3>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                  {selectedTimesheet.projects?.map((project, index) => (
                    <span key={index} style={{
                      background: '#2196F3',
                      color: 'white',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '14px'
                    }}>
                      {project}
                    </span>
                  ))}
                </div>
              </div>

              {/* Daily Breakdown */}
              {selectedTimesheet.entries && selectedTimesheet.entries.length > 0 && (
                <div>
                  <h3 style={{color: '#2196F3', marginBottom: '12px'}}>Daily Breakdown</h3>
                  <div style={{maxHeight: '300px', overflowY: 'auto'}}>
                    {selectedTimesheet.entries.map((entry, index) => (
                      <div key={index} style={{
                        background: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        borderLeft: '4px solid #2196F3'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                          <strong>{entry.date}</strong>
                          <span style={{color: '#2196F3', fontWeight: 'bold'}}>{entry.hours}h</span>
                        </div>
                        <div style={{fontSize: '14px', color: '#666'}}>
                          <div><strong>Project:</strong> {entry.project_title}</div>
                          {entry.description && (
                            <div style={{marginTop: '4px'}}><strong>Description:</strong> {entry.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                marginTop: '24px',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button 
                  onClick={handleCloseViewPopup}
                  style={{
                    padding: '10px 24px',
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ FIXED: Edit Popup - Day-by-Day Editing */}
        {showEditPopup && selectedTimesheet && (
          <div className="consult-popup-overlay" onClick={handleCloseEditPopup}>
            <div className="consult-popup-content" onClick={(e) => e.stopPropagation()} style={{
              width: window.innerWidth <= 768 ? '90%' : window.innerWidth <= 1024 ? '85%' : '800px',
              maxWidth: '800px',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '20px' : '32px',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px',
                borderBottom: '2px solid #2196F3',
                paddingBottom: '16px'
              }}>
                <div>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#2196F3',
                    fontFamily: 'Albert Sans',
                  }}>Edit Timesheet</h2>
                  {selectedTimesheet && (
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '14px',
                      color: '#666',
                      fontFamily: 'Albert Sans'
                    }}>
                      {selectedTimesheet.user_name}  
                    </p>
                  )}

                  {selectedTimesheet && (
                      <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '14px',
                      color: '#666',
                      fontFamily: 'Albert Sans'
                    }}>
                      {selectedTimesheet.user_email}
                    </p>
                  )}
                </div>
                <button 
                  onClick={handleCloseEditPopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Week Info */}
              <div style={{
                background: '#e3f2fd',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <strong>Period:</strong> {selectedTimesheet.week_display}
                  </div>
                  <div>
                    <strong>Total Days:</strong> {editDayEntries.length}
                  </div>
                  <div>
                    <strong>Total Hours:</strong> {editDayEntries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0).toFixed(1)}h
                  </div>
                </div>
              </div>

              {/* Day-by-Day Edit Form */}
              <div style={{
                display: 'grid',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {editDayEntries.map((dayEntry, index) => (
                  <div key={index} style={{
                    background: '#f8f9fa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '2px solid #e0e0e0'
                  }}>
                    {/* Date Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: '1px solid #ddd'
                    }}>
                      <strong style={{color: '#2196F3', fontSize: '16px'}}>
                        📅 {new Date(dayEntry.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </strong>
                    </div>

                    {/* Hours */}
                    <div style={{display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : window.innerWidth <= 900 ? 'repeat(2, 1fr)' : '1fr 1fr 1fr', gap: '12px', marginBottom: '12px'}}>
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          color: '#555E67',
                          fontSize: '12px',
                          fontWeight: '500',
                          fontFamily: 'Albert Sans'
                        }}>
                          Hours <span style={{color: '#DC2626'}}>*</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="24"
                          step="0.5"
                          value={dayEntry.hours}
                          onChange={(e) => handleDayEntryChange(index, 'hours', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #ECEDF0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Albert Sans',
                            backgroundColor: '#fff'
                          }}
                        />
                      </div>
                    </div>

                    {/* Project Title */}
                    <div style={{marginBottom: '12px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        color: '#555E67',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: 'Albert Sans'
                      }}>
                        Project Title <span style={{color: '#DC2626'}}>*</span>
                      </label>
                      <input
                        type="text"
                        value={dayEntry.project_title}
                        onChange={(e) => handleDayEntryChange(index, 'project_title', e.target.value)}
                        placeholder="Enter project title"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ECEDF0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'Albert Sans',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        color: '#555E67',
                        fontSize: '12px',
                        fontWeight: '500',
                        fontFamily: 'Albert Sans'
                      }}>
                        Description
                      </label>
                      <textarea
                        value={dayEntry.description}
                        onChange={(e) => handleDayEntryChange(index, 'description', e.target.value)}
                        rows="3"
                        placeholder="Work description for this day"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ECEDF0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'Albert Sans',
                          resize: 'vertical',
                          backgroundColor: '#fff'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div style={{
                background: '#fff3cd',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #ffc107'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <strong>Total Hours Across All Days:</strong> {editDayEntries.reduce((sum, e) => sum + parseFloat(e.hours || 0), 0).toFixed(1)}h
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <button 
                  onClick={handleCloseEditPopup}
                  style={{
                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                    padding: '12px 24px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'Albert Sans'
                  }}
                >
                  Cancel
                </button>
                
                <button 
                  onClick={handleSaveDayByDayEdit}
                  disabled={actionLoading === selectedTimesheet.submission_id}
                  style={{
                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                    padding: '12px 32px',
                    border: 'none',
                    borderRadius: '8px',
                    background: actionLoading === selectedTimesheet.submission_id ? '#ccc' : '#23b84e',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: actionLoading === selectedTimesheet.submission_id ? 'not-allowed' : 'pointer',
                    fontFamily: 'Albert Sans',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {actionLoading === selectedTimesheet.submission_id ? (
                    <>
                      <span>⏳</span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ✅ NEW: Approve Popup - Day-by-Day Review */}
        {showApprovePopup && selectedTimesheet && (
          <div className="consult-popup-overlay" onClick={handleCloseApprovePopup}>
            <div className="consult-popup-content" onClick={(e) => e.stopPropagation()} style={{
              width: window.innerWidth <= 768 ? '90%' : window.innerWidth <= 1024 ? '85%' : '800px',
              maxWidth: '800px',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '20px' : '32px',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px',
                borderBottom: '2px solid #4CAF50',
                paddingBottom: '16px'
              }}>
                <div>
                  <h2 style={{ 
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#4CAF50',
                    fontFamily: 'Albert Sans',
                  }}>Approve Timesheet</h2>
                  {selectedTimesheet && (
                    <>
                      <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '14px',
                        color: '#666',
                        fontFamily: 'Albert Sans'
                      }}>
                        {selectedTimesheet.user_name}  
                      </p>
                      <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '14px',
                        color: '#666',
                        fontFamily: 'Albert Sans'
                      }}>
                        {selectedTimesheet.user_email}
                      </p>
                    </>
                  )}
                </div>
                <button 
                  onClick={handleCloseApprovePopup}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Week Info */}
              <div style={{
                background: '#e8f5e9',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #4CAF50'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <strong>Period:</strong> {selectedTimesheet.week_display}
                  </div>
                  <div>
                    <strong>Total Days:</strong> {selectedTimesheet.entries?.length || 0}
                  </div>
                  <div>
                    <strong>Total Hours:</strong> {selectedTimesheet.total_hours}h
                  </div>
                </div>
              </div>

              {/* Day-by-Day View (Read-Only) */}
              <div style={{
                display: 'grid',
                gap: '16px',
                marginBottom: '24px'
              }}>
                {selectedTimesheet.entries && selectedTimesheet.entries.length > 0 ? (
                  selectedTimesheet.entries.map((dayEntry, index) => (
                    <div key={index} style={{
                      background: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '12px',
                      border: '2px solid #e0e0e0'
                    }}>
                      {/* Date Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                        paddingBottom: '12px',
                        borderBottom: '1px solid #ddd'
                      }}>
                        <strong style={{color: '#4CAF50', fontSize: '16px'}}>
                          📅 {new Date(dayEntry.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </strong>
                        <span style={{
                          background: '#4CAF50',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}>
                          {dayEntry.hours}h
                        </span>
                      </div>

                      {/* Hours (Read-Only) */}
                      <div style={{display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : window.innerWidth <= 900 ? 'repeat(2, 1fr)' : '1fr 1fr', gap: '12px', marginBottom: '12px'}}>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            color: '#555E67',
                            fontSize: '12px',
                            fontWeight: '500',
                            fontFamily: 'Albert Sans'
                          }}>
                            Hours
                          </label>
                          <div style={{
                            padding: '10px',
                            background: '#fff',
                            border: '1px solid #ECEDF0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Albert Sans',
                            color: '#333'
                          }}>
                            {dayEntry.hours}h
                          </div>
                        </div>
                      </div>

                      {/* Project Title (Read-Only) */}
                      <div style={{marginBottom: '12px'}}>
                        <label style={{
                          display: 'block',
                          marginBottom: '6px',
                          color: '#555E67',
                          fontSize: '12px',
                          fontWeight: '500',
                          fontFamily: 'Albert Sans'
                        }}>
                          Project Title
                        </label>
                        <div style={{
                          padding: '10px',
                          background: '#fff',
                          border: '1px solid #ECEDF0',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontFamily: 'Albert Sans',
                          color: '#333'
                        }}>
                          {dayEntry.project_title || 'General Work'}
                        </div>
                      </div>

                      {/* Description (Read-Only) */}
                      {dayEntry.description && (
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '6px',
                            color: '#555E67',
                            fontSize: '12px',
                            fontWeight: '500',
                            fontFamily: 'Albert Sans'
                          }}>
                            Description
                          </label>
                          <div style={{
                            padding: '10px',
                            background: '#fff',
                            border: '1px solid #ECEDF0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'Albert Sans',
                            color: '#333',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {dayEntry.description}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    No entries found for this timesheet
                  </div>
                )}
              </div>

              {/* Comments Section
              <div style={{
                background: '#fff3cd',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid #ffc107'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#555E67',
                  fontSize: '12px',
                  fontWeight: '500',
                  fontFamily: 'Albert Sans'
                }}>
                  Approval Comments (Optional)
                </label>
                <textarea
                  value={approveComments}
                  onChange={(e) => setApproveComments(e.target.value)}
                  placeholder="Add any comments before approving..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Albert Sans',
                    resize: 'vertical',
                    backgroundColor: '#fff'
                  }}
                />
              </div> */}

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid #e0e0e0'
              }}>
                <button 
                  onClick={handleCloseApprovePopup}
                  style={{
                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                    padding: '12px 24px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'Albert Sans'
                  }}
                >
                  Cancel
                </button>
                
                <button 
                  onClick={handleConfirmApprove}
                  disabled={actionLoading === selectedTimesheet.submission_id}
                  style={{
                    width: window.innerWidth <= 768 ? '100%' : 'auto',
                    padding: '12px 32px',
                    border: 'none',
                    borderRadius: '8px',
                    background: actionLoading === selectedTimesheet.submission_id ? '#ccc' : '#4CAF50',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: actionLoading === selectedTimesheet.submission_id ? 'not-allowed' : 'pointer',
                    fontFamily: 'Albert Sans',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {actionLoading === selectedTimesheet.submission_id ? (
                    <>
                      <span>⏳</span>
                      <span>Approving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      <span>Confirm Approval</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            position: 'fixed',
            bottom: '10px',
            right: '10px',
            backgroundColor: '#f3f4f6',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '11px',
            color: '#6b7280',
            maxWidth: '300px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <strong style={{color: '#2196F3'}}>Debug Info:</strong><br/>
            Environment: {process.env.NODE_ENV}<br/>
            Backend URL: {BACKEND_URL}<br/>
            Timesheets: {timesheets.length}<br/>
            Loading: {loading ? 'Yes' : 'No'}<br/>
            Error: {error ? 'Yes' : 'No'}
          </div>
        )}
      </div>    
    </div>
  );
};

export default TimesheetPage;